import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  BoostFundedGuestOffer,
  OfferAvailabilityStatus,
  RankedPromoOfferDiscount,
  useApplyBoostFundedOfferMutation,
  useGuestQuery,
  useRemoveBoostFundedOfferMutation
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'
import useTracker from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useCheckout } from 'src/public/components/online_ordering/CheckoutContext'
import { useGiftCard } from 'src/public/components/online_ordering/GiftCardContext'
import { useOffers } from 'src/public/components/online_ordering/OffersContext'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'
import {
  Modal,
  ModalContent,
  ModalOverlay,
  useModal
} from 'src/shared/components/common/modal'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { alertSuccess, alertError } from 'src/shared/js/alertUtils'
import { t } from 'src/translations'

import config from 'config'

import { useGiftCardInquiryReCaptchaToken } from 'src/public/components/online_ordering/recaptcha'
import { getAppliedBoostOfferGuid } from 'public/components/default_template/offers/appliedFundedOffers'

import DiscountPill, {
  DiscountPillState,
  DiscountPillType
} from './DiscountPill'
import DiscountsModal, { DiscountsModalType } from './DiscountsModal'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'

const OffersSection = () => {
  const { achievedOffers } = useOffers()
  const { cart, applyPromoCode, removePromoCode, cartGuid, refetchCart } =
    useCart()
  const { isOpen, onClose, onOpen } = useModal()
  const [applyBoostFundedOffer] = useApplyBoostFundedOfferMutation()
  const [removeBoostFundedOffer] = useRemoveBoostFundedOfferMutation()
  const { customer } = useCustomer()
  const [pendingBoostOfferId, setPendingBoostOfferId] = useState<string>()
  const {
    startGeneration: startReCaptchaGeneration,
    getToken,
    isAwaitingToken
  } = useGiftCardInquiryReCaptchaToken()

  const currentAppliedOffer = useMemo(
    () => cart?.order?.discounts?.restaurantDiscount?.promoCode?.toUpperCase(),
    [cart?.order?.discounts?.restaurantDiscount?.promoCode]
  )
  const currentAppliedBoostOffer = useMemo(
    () => getAppliedBoostOfferGuid(cart?.appliedFundedOffers),
    [cart]
  )

  const applyOffer = useCallback(
    async (offer: string) => {
      const applyError = await applyPromoCode(offer)
      if (applyError) {
        alertError(t('offers-section.error-applying-promo-code'), {
          containerId: 'giftCardPromoCodeToastContainer'
        })
      } else {
        alertSuccess(t('offers-section.offer-successfully-applied'), {
          containerId: 'giftCardPromoCodeToastContainer'
        })
        onClose()
      }
    },
    [applyPromoCode, onClose]
  )

  const removeOffer = useCallback(async () => {
    const removeError = await removePromoCode()
    if (removeError) {
      alertError(t('offers-section.error-removing-promo-code'), {
        containerId: 'giftCardPromoCodeToastContainer'
      })
    }
  }, [removePromoCode])

  const onClickOffer = useCallback(
    async (offer: RankedPromoOfferDiscount) => {
      if (currentAppliedOffer === offer.promoCode) {
        await removeOffer()
      } else {
        await applyOffer(offer.promoCode)
      }
    },
    [applyOffer, currentAppliedOffer, removeOffer]
  )

  const onClickBoostOffer = useCallback(
    async (offer: BoostFundedGuestOffer) => {
      if (!cartGuid) {
        return
      }

      const isRemoving = currentAppliedBoostOffer === offer.id
      let failed = false

      // Blocks the other pills too — overlapping cart writes contend for a row lock.
      setPendingBoostOfferId(offer.id)

      // These mutations return union types, so a business-level failure resolves
      // normally rather than throwing — the result has to be inspected. A
      // network-level failure still rejects.
      try {
        if (isRemoving) {
          const { data } = await removeBoostFundedOffer({
            variables: {
              input: {
                cartGuid
              }
            }
          })
          failed = data?.removeFundedOffer.__typename !== 'CartResponse'
        } else {
          // removeFundedOffer clears every funded offer including the free
          // delivery credit, so a swap applies over the previous offer instead.
          const { data } = await applyBoostFundedOffer({
            variables: {
              input: {
                cartGuid,
                offerGuid: offer.id
              }
            }
          })
          failed = data?.applyBoostFundedOffer.__typename !== 'CartResponse'
        }
      } catch (error) {
        failed = true
        reportError(
          isRemoving
            ? 'Error removing the applied boost funded offer.'
            : 'Error applying a boost funded offer.',
          error,
          { cartGuid, offerGuid: offer.id }
        )
      }

      if (failed) {
        alertError(
          isRemoving
            ? t('offers-section.error-removing-offer')
            : t('offers-section.error-applying-offer'),
          { containerId: 'giftCardPromoCodeToastContainer' }
        )
      }

      try {
        await refetchCart()
      } finally {
        setPendingBoostOfferId(undefined)
      }
    },
    [
      cartGuid,
      currentAppliedBoostOffer,
      applyBoostFundedOffer,
      removeBoostFundedOffer,
      refetchCart
    ]
  )

  // Gift Cards
  const {
    giftCardNumber,
    applyGiftCard,
    applyGiftCardExtension,
    giftCardError,
    removeGiftCard
  } = useGiftCard()
  const { ooRestaurant, toastProduct } = useRestaurant()
  const { data: guest } = useGuestQuery({
    fetchPolicy: 'network-only', // Force fetching first in case offers have been added before navigating to this page
    nextFetchPolicy: 'cache-and-network',
    variables: {
      restaurantId: ooRestaurant?.guid
    },
    skip: !customer
  })
  const tracker = useTracker()
  const { giftCardAppliedAmount } = useCheckout()
  const gcLast4 = useMemo(() => giftCardNumber.slice(-4), [giftCardNumber])
  const gcConfig = ooRestaurant?.giftCardConfig
  const prevGiftCardAppliedAmount: React.MutableRefObject<number> = useRef(0)
  const prevGiftCardNumber = useRef('')
  const isFirstRender = useRef(true)
  const firstOrThirdParty = gcConfig?.hasGiftCardsExtension ? '3P' : '1P'
  const acceptsGiftCards = useMemo(() => {
    const accepts3PGC =
      gcConfig?.hasGiftCardsExtension &&
      ![
        ToastProduct.OOBasic,
        ToastProduct.ToastLocal,
        ToastProduct.FreeOOToastLocal
      ].includes(toastProduct)
    return gcConfig?.redemptionAllowed || accepts3PGC
  }, [
    gcConfig?.hasGiftCardsExtension,
    gcConfig?.redemptionAllowed,
    toastProduct
  ])

  const applyGiftCardToCart = useCallback(
    async (giftCardNumber: string, code?: string) => {
      if (cartGuid && giftCardNumber && acceptsGiftCards) {
        if (gcConfig?.hasGiftCardsExtension) {
          tracker.track(TrackingEvents.CLICKED_APPLY_GIFT_CARD, {
            cartSource: cart?.cartSource,
            firstOrThirdParty,
            verificationCodeSubmitted: Boolean(code)
          })
          grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(
              config.reCaptchaEnterprise.siteKey,
              { action: 'giftcard_extension_inquiry' }
            )
            const tokenProto = `recaptcha:enterprise:${token}`
            await applyGiftCardExtension(
              cartGuid,
              giftCardNumber,
              tokenProto,
              code
            )
          })
        } else {
          tracker.track(TrackingEvents.CLICKED_APPLY_GIFT_CARD, {
            cartSource: cart?.cartSource,
            firstOrThirdParty,
            verificationCodeSubmitted: false
          })
          const rawToken = await getToken()
          const captchaToken = rawToken
            ? `recaptcha:enterprise:${rawToken}`
            : undefined
          await applyGiftCard(cartGuid, giftCardNumber, captchaToken)
        }
      }
    },
    [
      acceptsGiftCards,
      applyGiftCard,
      applyGiftCardExtension,
      cart,
      cartGuid,
      firstOrThirdParty,
      gcConfig?.hasGiftCardsExtension,
      getToken,
      tracker
    ]
  )

  const removeGiftCardFromCart = useCallback(() => {
    removeGiftCard()
  }, [removeGiftCard])

  useEffect(() => {
    if (prevGiftCardAppliedAmount.current !== giftCardAppliedAmount) {
      prevGiftCardAppliedAmount.current = giftCardAppliedAmount
      refetchCart({ totalGiftCardBalance: giftCardAppliedAmount })
    }
  }, [refetchCart, giftCardAppliedAmount])

  useEffect(() => {
    const prevValue = prevGiftCardNumber.current
    const giftCardNumberChanged = prevValue !== giftCardNumber
    const isMount = isFirstRender.current

    prevGiftCardNumber.current = giftCardNumber

    if (isMount) {
      isFirstRender.current = false
    }

    if (giftCardNumberChanged && giftCardNumber && !isMount) {
      alertSuccess(t('offers-section.gift-card-applied-successfully'), {
        containerId: 'giftCardPromoCodeToastContainer'
      })
      tracker.track(TrackingEvents.GIFT_CARD_APPLIED_SUCCESSFULLY, {
        cartSource: cart?.cartSource,
        firstOrThirdParty
      })
      setModalCurrentValue(undefined)
      onClose()
    } else if (giftCardError) {
      alertError(t('offers-section.error-applying-gift-card'), {
        containerId: 'giftCardPromoCodeToastContainer'
      })
      tracker.track(TrackingEvents.GIFT_CARD_ERROR, {
        cartSource: cart?.cartSource,
        firstOrThirdParty,
        error: giftCardError
      })
    }
  }, [cart, giftCardNumber, giftCardError, tracker, firstOrThirdParty, onClose])
  const [gcWarningModalOpen, setGCWarningModalOpen] = useState(false)

  const promoModifierText = useMemo(
    () =>
      currentAppliedOffer
        ? t('offers-section.update-promo-code')
        : t('offers-section.add-promo-code'),
    [currentAppliedOffer]
  )
  const gcModifierText = useMemo(
    () =>
      giftCardNumber.length !== 0
        ? t('offers-section.update-gift-card')
        : t('offers-section.add-gift-card'),
    [giftCardNumber]
  )
  const [modalType, setModalType] = useState<DiscountsModalType>(
    DiscountsModalType.OFFER
  )
  const [modalCurrentValue, setModalCurrentValue] = useState<
    string | undefined
  >(currentAppliedOffer)
  const [modalHeader, setModalHeader] = useState<string>('')
  const [modalDescription, setModalDescription] = useState<string | undefined>(
    undefined
  )
  const onApply = useCallback(
    async (primary: string, secondary: string) => {
      if (modalType === DiscountsModalType.OFFER) {
        await applyOffer(primary)
      } else {
        await applyGiftCardToCart(primary, secondary)
      }
    },
    [applyGiftCardToCart, applyOffer, modalType]
  )

  return (
    <div className='offersSectionContainer'>
      <div className='header'>
        {t('offers-section.offers-and-gifts-header')}
      </div>
      <div className='offerPills'>
        {currentAppliedOffer &&
        !achievedOffers?.find(
          (offer) => offer.promoCode === currentAppliedOffer
        ) ? (
          <DiscountPill
            pillKey='discount-promo-pill'
            text={currentAppliedOffer}
            state={DiscountPillState.ACTIVE}
            onClick={removeOffer}
          />
        ) : null}
        {guest?.guest?.savedOffers?.items?.length
          ? guest.guest.savedOffers.items
              .map((offer) => {
                const savedOffer = offer as BoostFundedGuestOffer
                // Mirrors the carousel in Offers.tsx: an offer that isn't
                // currently available can't be applied, so don't offer a pill.
                if (
                  savedOffer.availability?.availabilityCalculations
                    ?.currentAvailabilityStatus?.status !==
                  OfferAvailabilityStatus.Available
                ) {
                  return null
                }
                // Minimum spend is evaluated against the pre-tax subtotal, not the
                // order total, which includes tax, fees and tip.
                if (
                  savedOffer.minimumSpendAmount?.amount &&
                  parseFloat(savedOffer.minimumSpendAmount.amount) >=
                    (cart?.order?.preDiscountItemsSubtotal || 0)
                ) {
                  return null
                }

                return (
                  <DiscountPill
                    key={`saved-offer-pill-${savedOffer.id}`}
                    text={savedOffer.displayText || ''}
                    state={
                      currentAppliedBoostOffer === savedOffer.id
                        ? DiscountPillState.ACTIVE
                        : DiscountPillState.INACTIVE
                    }
                    loadingOverride={
                      Boolean(pendingBoostOfferId) &&
                      pendingBoostOfferId !== savedOffer.id
                    }
                    // Returned so the pill stays disabled for the mutation.
                    onClick={() => onClickBoostOffer(savedOffer)}
                  />
                )
              })
              .filter(Boolean)
          : null}
        {giftCardNumber.length !== 0 && (
          <DiscountPill
            pillKey='discount-gc-pill'
            text={t('offers-section.gift-card-last-four', { last4: gcLast4 })}
            state={DiscountPillState.ACTIVE}
            onClick={() => setGCWarningModalOpen(true)}
          />
        )}
        {achievedOffers?.map((offer) => (
          <DiscountPill
            key={`discount-offer-pill-${offer.bannerGuid}`}
            text={offer.bannerText}
            state={getDiscountPillState(offer, currentAppliedOffer)}
            onClick={() => onClickOffer(offer)}
            triggerDiningOptions={offer.discount?.triggerDiningOptions}
          />
        ))}
      </div>
      <div className='buttonContainer'>
        <DiscountPill
          text={`${promoModifierText} +`}
          type={DiscountPillType.BUTTON}
          onClick={() => {
            onOpen()
            setModalType(DiscountsModalType.OFFER)
            setModalHeader(t('offers-section.promo-code'))
            setModalDescription(t('offers-section.promo-code-description'))
            setModalCurrentValue(currentAppliedOffer)
          }}
          testId='add-promo-modal-btn'
        />
        {acceptsGiftCards && (
          <DiscountPill
            text={`${gcModifierText} +`}
            type={DiscountPillType.BUTTON}
            onClick={() => {
              onOpen()
              setModalHeader(t('offers-section.gift-card'))
              setModalDescription(undefined)
              setModalType(DiscountsModalType.GIFT_CARD)
              setModalCurrentValue(giftCardNumber)
              startReCaptchaGeneration()
            }}
            testId='add-gc-modal-btn'
          />
        )}
        <DiscountsModal
          header={modalHeader}
          description={modalDescription}
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          onApply={onApply}
          currentValue={modalCurrentValue}
          type={modalType}
          isAwaitingReCaptchaToken={isAwaitingToken}
        />
        <RemoveGCWarningModal
          isOpen={gcWarningModalOpen}
          setIsOpen={setGCWarningModalOpen}
          onConfirm={removeGiftCardFromCart}
        />
      </div>
    </div>
  )
}

const getDiscountPillState = (
  offer: RankedPromoOfferDiscount,
  currentAppliedDiscount: string | undefined
) => {
  if (offer.percentCompleteInt < 100) {
    return DiscountPillState.LOCKED
  }
  if (currentAppliedDiscount === offer.promoCode.toUpperCase()) {
    return DiscountPillState.ACTIVE
  }
  return DiscountPillState.INACTIVE
}

interface RemoveGCWarningModalProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  onConfirm: () => void
}
const RemoveGCWarningModal = ({
  isOpen,
  setIsOpen,
  onConfirm
}: RemoveGCWarningModalProps) => {
  const onClose = () => setIsOpen(false)
  return (
    <Modal isOpen={isOpen} onClose={onClose} testId='remove-gc-modal'>
      <ModalOverlay />
      <ModalContent>
        <div className='discountsModal'>
          <div className='header'>
            {t('offers-section.remove-gift-card-question')}
          </div>
          <div className='description'>
            {t('offers-section.remove-gift-card-description')}
          </div>
          <div className='buttonContainer'>
            <button
              className='modalButton cancel'
              type='button'
              onClick={onClose}
            >
              {t('offers-section.cancel')}
            </button>
            <button
              className='modalButton confirm'
              type='button'
              onClick={() => {
                onConfirm()
                onClose()
              }}
            >
              {t('offers-section.yes-remove')}
            </button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}

export default OffersSection
